import { Phone, Video, MessageSquare, MoreVertical, X, PhoneIncoming, PhoneOutgoing, PhoneMissed } from "lucide-react"
import type { CallRecord } from "./types"
import { AvatarPhoto } from "./avatar-photo"

interface CallInfoPanelProps {
  record: CallRecord
  onClose?: () => void
  onMessage?: () => void
  onAudioCall?: () => void
  onVideoCall?: () => void
}

function Avatar({ name, imageUrl, contactId, size = 72 }: { name: string; imageUrl?: string; contactId?: string; size?: number }) {
  const initials = name
    .split(" ")
    .map(w => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase()
  const colors = [
    "bg-primary/20 text-primary",
    "bg-violet-500/20 text-violet-600",
    "bg-emerald-500/20 text-emerald-600",
    "bg-amber-500/20 text-amber-600",
  ]
  const colorIdx = name.charCodeAt(0) % colors.length

  return (
    <div
      className={`rounded-full flex items-center justify-center font-semibold shrink-0 overflow-hidden ${colors[colorIdx]}`}
      style={{ width: size, height: size }}
    >
      <AvatarPhoto src={imageUrl} alt={name} className="w-full h-full object-cover" fallback={<span className="text-xl">{initials}</span>} contactId={contactId} />
    </div>
  )
}

function DirectionIcon({ record }: { record: CallRecord }) {
  if (record.isMissedCall) return <PhoneMissed className="w-4 h-4 text-destructive" />
  if (record.callDir === "out") return <PhoneOutgoing className="w-4 h-4 text-muted-foreground" />
  return <PhoneIncoming className="w-4 h-4 text-green-500" />
}


export function CallInfoPanel({ record, onClose, onMessage, onAudioCall, onVideoCall }: CallInfoPanelProps) {
  const history = [
    {
      id: "h1",
      typeLabel: record.isMissedCall ? "Missed" : record.callDir === "out" ? "Outgoing" : "Incoming",
      time: "6:39 PM",
      status: record.isMissedCall ? "Not answered" : record.duration ?? "2 min 15 sec",
      isVideo: record.isVideoCall,
    },
    {
      id: "h2",
      typeLabel: "Outgoing",
      time: "6:52 PM",
      status: "Not answered",
      isVideo: record.isVideoCall,
    },
  ]

  return (
    <div className="flex flex-col h-full bg-card overflow-hidden">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <p className="text-base font-semibold text-foreground">Call info</p>
        </div>
        <div className="flex items-center gap-1">
          <button className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" type="button">
            <MoreVertical className="w-4.5 h-4.5" />
          </button>
          {onClose && (
            <button
              className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              onClick={onClose}
              type="button"
            >
              <X className="w-4.5 h-4.5" />
            </button>
          )}
        </div>
      </div>

      {/* Profile */}
      <div className="px-6 py-6">
        <div className="flex items-center gap-5">
          <Avatar name={record.name} imageUrl={record.imageUrl} contactId={record.peerId} size={84} />
          <div className="min-w-0">
            <p className="text-lg font-semibold text-foreground truncate">{record.name}</p>
            <div className="flex items-center gap-2 mt-1">
              <DirectionIcon record={record} />
              <span className="text-sm text-muted-foreground">{record.time}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-stretch gap-3 mt-6">
          <button
            className="flex-1 border border-border rounded-2xl px-3 py-4 hover:bg-muted/40 transition-colors"
            type="button"
            onClick={onMessage}
          >
            <div className="flex flex-col items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              <span className="text-xs text-muted-foreground">Message</span>
            </div>
          </button>
          <button
            className="flex-1 border border-border rounded-2xl px-3 py-4 hover:bg-muted/40 transition-colors"
            type="button"
            onClick={onAudioCall}
          >
            <div className="flex flex-col items-center gap-2">
              <Phone className="w-5 h-5 text-primary" />
              <span className="text-xs text-muted-foreground">Audio</span>
            </div>
          </button>
          <button
            className="flex-1 border border-border rounded-2xl px-3 py-4 hover:bg-muted/40 transition-colors"
            type="button"
            onClick={onVideoCall}
          >
            <div className="flex flex-col items-center gap-2">
              <Video className="w-5 h-5 text-primary" />
              <span className="text-xs text-muted-foreground">Video</span>
            </div>
          </button>
        </div>
      </div>

      <div className="h-px bg-border" />

      {/* History */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <p className="text-sm font-medium text-muted-foreground mb-4">January 25</p>
        <div className="space-y-5">
          {history.map(h => (
            <div key={h.id} className="flex items-start gap-4">
              <div className={`p-2 rounded-xl ${h.typeLabel === "Missed" ? "bg-destructive/10" : "bg-primary/10"}`}>
                {h.isVideo ? (
                  <Video className={`w-5 h-5 ${h.typeLabel === "Missed" ? "text-destructive" : "text-primary"}`} />
                ) : (
                  <Phone className={`w-5 h-5 ${h.typeLabel === "Missed" ? "text-destructive" : "text-primary"}`} />
                )}
              </div>
              <div className="flex-1">
                <p className={`text-sm font-medium ${h.typeLabel === "Missed" ? "text-destructive" : "text-foreground"}`}>
                  {h.typeLabel}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{h.time}</p>
              </div>
              <p className="text-xs text-muted-foreground">{h.status}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
